import type { FormStep, OpenerTrigger, PageKind } from './types';

export type DeviceClass = 'desktop' | 'mobile';

export const INACTIVITY_MS = 20_000;
export const INACTIVITY_EVENTS = ['scroll', 'touchstart', 'touchmove', 'pointerdown', 'keydown', 'input'] as const;
export const FORM_LEAVING_TRIGGERS: readonly OpenerTrigger[] = ['exit_intent', 'focus_loss', 'inactivity'];

const ARTICLE_PREFIX = '/guide/assurance-pret-immobilier';
// The public /assurance-pret-immobilier redirects to this WordPress pillar page of the guide, which acts as the product landing.
const LANDING_PAGE = '/guide/assurance-pret-immobilier';
const LANDING_PREFIXES = ['/assurance-pret-immobilier', '/simulation-assurance-pret-immobilier'];
const FORM_PREFIXES = [
  '/devis-assurance-pret-immobilier',
  '/parcours-assurance-pret-immobilier',
  '/coordonnees-assurance-pret-immobilier',
  '/adhesion-assurance-pret-immobilier',
  '/resultats-assurance-pret-immobilier',
  '/mon-compte-assurance-pret-immobilier',
];

export function guideChatKindForPath(pathname: string): PageKind | null {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  if (path === LANDING_PAGE) return 'landing';
  if (path.startsWith(ARTICLE_PREFIX)) return 'article';
  if (LANDING_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))) return 'landing';
  if (FORM_PREFIXES.some((p) => path.startsWith(p))) return 'form';
  return null;
}

// One proactive opener per page view, or per wizard step on the form.
export function openerKey(pathname: string, step: FormStep | null | undefined): string {
  return step ? `${pathname}#${step}` : pathname;
}

// Phones have no exit intent and back or tab switches misfire there, so inactivity is their only leaving signal.
export function leaveTriggersFor(kind: PageKind, device: DeviceClass): OpenerTrigger[] {
  if (device === 'mobile') return ['inactivity'];
  return kind === 'form' ? ['exit_intent', 'focus_loss'] : ['exit_intent'];
}

export function isFormLeavingTrigger(trigger: OpenerTrigger): boolean {
  return FORM_LEAVING_TRIGGERS.includes(trigger);
}

export function watchInactivity(target: EventTarget, onIdle: () => void, ms: number = INACTIVITY_MS): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const clear = () => {
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };
  const arm = () => {
    clear();
    timer = setTimeout(() => {
      timer = null;
      onIdle();
    }, ms);
  };
  INACTIVITY_EVENTS.forEach((event) => target.addEventListener(event, arm, { passive: true }));
  arm();
  return () => {
    clear();
    INACTIVITY_EVENTS.forEach((event) => target.removeEventListener(event, arm));
  };
}
